import { defineStore } from 'pinia';
import { createEventStream } from 'src/core/services/eventStream';
import type {
  EventStreamConnection,
  EventStreamOptions,
} from 'src/core/services/eventStream';

type EventStreamStore = {
  connections: Set<EventStreamConnection>;
};

/**
 * Registry store for live SSE connections — the streaming counterpart to
 * `useApiStore`. It owns every open event stream so they can be torn down in
 * one place (e.g. on logout), preventing dangling connections that would keep
 * sending the old bearer token after sign-out.
 */
export const useEventStreamStore = defineStore('eventStream', {
  state: () =>
    ({
      connections: new Set<EventStreamConnection>(),
    } as EventStreamStore),

  actions: {
    /**
     * Open a typed event stream and track it so it can be torn down on logout.
     * The connection removes itself from the registry once it reaches its
     * terminal `closed` state, and the caller's `onClose` is still invoked.
     * @param options - The event stream options forwarded to `createEventStream`.
     * @returns The handle to the live connection.
     */
    connect<TEvent>(
      options: EventStreamOptions<TEvent>
    ): EventStreamConnection {
      // Holder so the onClose closure can reach the handle that createEventStream
      // returns synchronously below (the callback only ever fires afterwards).
      const handle = {} as { connection: EventStreamConnection };
      handle.connection = createEventStream<TEvent>({
        ...options,
        onClose: (reason) => {
          this.connections.delete(handle.connection);
          options.onClose?.(reason);
        },
      });
      this.connections.add(handle.connection);
      return handle.connection;
    },

    /**
     * Close every active stream. Call on logout to avoid dangling connections.
     */
    closeAll() {
      this.connections.forEach((c) => c.close());
      this.connections.clear();
    },
  },
});
